import React, { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { Images, useModal, User } from 'utils';
import { httpRequestsOnLoadActions, UploadServices } from 'store';
import { AddCircleBlue } from '../../buttons';
import { NoItemsYet } from '../../noItem';
import { UploadVideo } from './uploadVideo';
import { VideoPlayer } from './videoPlayer';
import { TextRow } from '../tableRows';
import { CHUNK_SIZE, videoStatusEnums } from './constants';
import { SimpleTooltip } from '../../tooltip';
import { CustomDelete } from '../../modal';
import { NotError } from '../../screens';
import { LoadInProgress } from '../../loader';

const DELETE_ACTION_TYPE = 'DELETE_VIDEO';
const CANCEL_ACTION_TYPE = 'CANCEL_VIDEO_UPLOAD';
export const VideoFiles = ({ resourceId, model, videos, afterSuccess }) => {
    const { open, close } = useModal();
    const { userInfo } = User();
    const dispatch = useDispatch();
    const timeoutRef = useRef(null);

    useEffect(() => {
        const progressVideo = videos?.find((i) => i?.status === videoStatusEnums.IN_PROGRESS);

        if (progressVideo) {
            if (timeoutRef.current) clearTimeout(timeoutRef.current);

            timeoutRef.current = setTimeout(() => {
                afterSuccess();
                timeoutRef.current = null;
            }, 5000);
        }

        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
        };
    }, [videos, afterSuccess]);

    const cancelUploaded = async (item) => {
        dispatch(httpRequestsOnLoadActions.appendLoading(CANCEL_ACTION_TYPE));
        // In the new direct-S3 model there's no server-side "in-progress"
        // record to abort — DB entries only appear after a successful
        // upload. "Cancel" and "Delete" are now the same operation.
        await UploadServices.DeleteVideo(resourceId, item?.id, model)
            .then((res) => {
                toast(<NotError errMessage={'Upload canceled successfully!'} />);
                afterSuccess();
                close();
            })
            .catch(() => {})
            .finally(() => {
                dispatch(httpRequestsOnLoadActions.removeLoading(CANCEL_ACTION_TYPE));
            });
    };

    const deleteUploaded = async (item) => {
        dispatch(httpRequestsOnLoadActions.appendLoading(DELETE_ACTION_TYPE));
        await UploadServices.DeleteVideo(resourceId, item?.id, model)
            .then((res) => {
                toast(<NotError errMessage={'Video deleted successfully!'} />);
                afterSuccess();
                close();
            })
            .catch(() => {})
            .finally(() => {
                dispatch(httpRequestsOnLoadActions.removeLoading(DELETE_ACTION_TYPE));
            });
    };
    const handleAction = (item, name) => {
        if (name === 'cancelUpload') {
            open(
                <CustomDelete
                    info="Cancel Upload?"
                    text="This upload will be stopped and removed from the list"
                    handleDel={() => cancelUploaded(item)}
                    actionType={CANCEL_ACTION_TYPE}
                    afterSuccess={afterSuccess}
                    body={
                        <div className="deleting-client-info-box" style={{ alignItems: 'flex-start' }}>
                            <img style={{ marginTop: 4 }} src={Images.videos} alt="icon" />
                            <p style={{ fontWeight: 600 }}>{item?.name}</p>
                        </div>
                    }
                />
            );
        }
        if (name === 'deleteFile') {
            open(
                <CustomDelete
                    info="Delete Video?"
                    text="This video will be permanently removed from this goal."
                    handleDel={() => deleteUploaded(item)}
                    actionType={DELETE_ACTION_TYPE}
                    afterSuccess={afterSuccess}
                    body={
                        <div className="deleting-client-info-box" style={{ alignItems: 'flex-start' }}>
                            <img style={{ marginTop: 4 }} src={Images.videos} alt="icon" />
                            <p style={{ fontWeight: 600 }}>{item?.name}</p>
                        </div>
                    }
                />
            );
        }
    };

    const renderProgress = (item) => {
        if (!item?.fileSize || item?.lastPartNumber == null) return 0;
        const totalChunks = Math.ceil(item.fileSize / CHUNK_SIZE);
        const uploadedChunks = item.lastPartNumber + 1;
        let progress = (uploadedChunks / totalChunks) * 100;
        progress = Math.min(progress, 100);
        return progress.toFixed(0);
    };

    return (
        <div>
            <div className="header-action-btn">
                    <AddCircleBlue
                        type="button"
                        text={'Upload Video'}
                        icon={Images.uploadDockCloud}
                        handleClick={() => open(<UploadVideo afterSuccess={afterSuccess} resourceId={resourceId} model={model} />)}
                    />
            </div>

            {videos?.length ? (
                <div className="video-file-wrapper">
                    {videos?.map((i, j) => (
                        <div key={j} className="video-file-box">
                            <div
                                className="video-background-image-wrapper"
                                style={{
                                    backgroundImage: i?.backgroundImage?.url ? `url("${i.backgroundImage.url}")` : undefined,
                                    backgroundSize: 'cover',
                                    backgroundPosition: 'center',
                                    width: '100%',
                                    height: '136px',
                                }}
                            >
                                {(i?.status === videoStatusEnums.IN_PROGRESS || i?.status === videoStatusEnums.FAILED) && (
                                    <div
                                        style={{
                                            position: 'absolute',
                                            inset: 0,
                                            background: 'rgba(239,239,239,0.5)',
                                            borderRadius: 'inherit',
                                            zIndex: 1,
                                        }}
                                    />
                                )}

                                {i?.status === videoStatusEnums.IN_PROGRESS && i?.creator?.id === userInfo?.id && (
                                    <SimpleTooltip title="Cancel upload" placement="top" arrow>
                                        <button onClick={() => handleAction(i, 'cancelUpload')} className="video-action-box">
                                            <img src={Images.remove} alt="icon" />
                                        </button>
                                    </SimpleTooltip>
                                )}

                                {(i?.status === videoStatusEnums.COMPLETED || i?.status === videoStatusEnums.FAILED) &&
                                    i?.creator?.id === userInfo?.id && (
                                        <SimpleTooltip title="Delete video" placement="top" arrow>
                                            <button onClick={() => handleAction(i, 'deleteFile')} className="video-action-box">
                                                <img src={Images.redTrash} alt="icon" />
                                            </button>
                                        </SimpleTooltip>
                                    )}

                                {i?.status === videoStatusEnums.IN_PROGRESS ? (
                                    <div style={{ position: 'relative', zIndex: 2 }}>
                                        <LoadInProgress loadValue={i?.progress} />
                                    </div>
                                ) : (
                                    i?.status === videoStatusEnums.COMPLETED && (
                                        <button
                                            className="reset-btn-by-default"
                                            onClick={() =>
                                                open(<VideoPlayer videoKey={i?.file?.key} />, {
                                                    styles: {
                                                        wrapperStyle: { padding: '0px', borderRadius: '8px', background: 'black' },
                                                        bodyStyle: { padding: '0px', borderRadius: '8px', background: 'black' },
                                                    },
                                                })
                                            }
                                        >
                                            <img src={Images.playButton} alt="playButton" />
                                        </button>
                                    )
                                )}
                            </div>

                            <div className="full-width">
                                <div className="video-file-name full-width">
                                    <TextRow name={i?.name} color={'#172B4D'} fontWeight={600} textWidth={8} />
                                </div>
                                {i?.description && (
                                    <div className="video-file-description">
                                        <TextRow name={i?.description} color="#475467" />
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <NoItemsYet
                    text={'No instructional videos yet'}
                    subTitle={'Upload videos that demonstrate how to implement this goal.'}
                    image={Images.emptyFolder}
                />
            )}
        </div>
    );
};
